import "./application";
import '../utils/helpers/datetimeLocalization';
import '../utils/bugsnag';
import '../apps/on_boarding/main.ts';
import '../apps/plan_trail/main';
import '../apps/comments/main';
import '../apps/download_modal/main';
import '../apps/flyover_preview/main.ts';
import '../apps/import_hike/main.ts';
import '../apps/embed_builder/main';
import { OnBoardingGoal } from '../utils/models/OnBoardingGoal';
import { RevenueCatWrapper } from '../utils/RevenueCatWrapper';
import graph_client from '../utils/graphql/graph_client';
import { DestroyHike, SubmitHikeForOfficial } from '../utils/graphql/hikes-graph';

declare global {
  interface Window {
    printingUrl?: string;
  }
}

const gpxDownloadButtons = document.querySelectorAll('.gpx-download');
const addToTrailListButton = document.querySelector('.add-to-trail-list');
const completeHikeOnboardingButtons = document.querySelectorAll('.complete-hike-onboarding');
const dropdownItems = document.querySelectorAll('.dropdown-item');
const copyToHikesLoginButtons = document.querySelectorAll('.copy-to-hikes-login');

const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content;
const printMapDownloadButtons = document.querySelectorAll('.print-map-download');

if (gpxDownloadButtons) {
  gpxDownloadButtons.forEach((gpxDownloadButton) => {
    gpxDownloadButton.addEventListener('click', () => {
      window.openDownloadModal();
    });
  });
}

if (addToTrailListButton) {
  addToTrailListButton.addEventListener('click', () => {
    window.onBoardingVue.launchFor(OnBoardingGoal.AddTrailToList);
  });
}

if (completeHikeOnboardingButtons) {
  completeHikeOnboardingButtons.forEach((button) => {
    button.addEventListener('click', () => {
      window.onBoardingVue?.launchFor(OnBoardingGoal.CompleteHike);
    });
  });
}

if (dropdownItems) {
  dropdownItems.forEach((dropdownItem) => {
    if (dropdownItem.textContent?.trim() === 'Send to Garmin') {
      dropdownItem.addEventListener('click', () => {
        if (window.openDownloadModal) {
          window.openDownloadModal('gps');
        }
      });
    }
    if (dropdownItem.textContent?.trim() === 'Report an issue') {
      dropdownItem.addEventListener('click', async () => {
        const obfuscatedSlug = window.obfuscated_slug;
        const slug = window.slug;
        const regionSlug = window.region_slug;
        const countrySlug = window.country_slug;

        let reportUrl: string;
        if (countrySlug && regionSlug && slug) {
          reportUrl = `/hikes/${countrySlug}/${regionSlug}/${slug}/report_issue`;
        } else if (obfuscatedSlug) {
          reportUrl = `/hikes/users/${obfuscatedSlug}/report_issue`;
        } else {
          alert('Unable to report issue at this time.');
          return;
        }

        try {
          const response = await fetch(reportUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken || '',
              'X-Requested-With': 'XMLHttpRequest'
            }
          });

          if (response.ok) {
            alert('Thank you for reporting this issue. Our team has been notified.');
          } else {
            alert('Unable to report issue at this time. Please try again later.');
          }
        } catch (error) {
          console.error('Error reporting issue:', error);
          alert('Unable to report issue at this time. Please try again later.');
        }
      });
    }
  });
}

if (copyToHikesLoginButtons) {
  copyToHikesLoginButtons.forEach((button) => {
    const triggerLoginFlow = () => {
      const onboarding = window.onBoardingVue;
      if (onboarding && typeof onboarding.launchFor === 'function') {
        onboarding.launchFor(OnBoardingGoal.CreateAccount);
        return;
      }

      window.location.href = '/login';
    };

    button.addEventListener('click', triggerLoginFlow);
    button.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        triggerLoginFlow();
      }
    });
  });
}

if (printMapDownloadButtons) {
  printMapDownloadButtons.forEach((printMapDownloadButton) => {
    printMapDownloadButton.addEventListener('click', async () => {
      const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();
      if (isProOrProPlus) {
        if (window.printingUrl) {
          window.location.href = window.printingUrl;
        } else {
          console.warn('No print map URL is available for this hike.');
        }
      } else {
        window.location.href = '/purchases';
      }
    });
  });
}

// Delete hike handler
const deleteHikeButton = document.getElementById('delete-hike');
if (deleteHikeButton) {
  deleteHikeButton.addEventListener('click', async () => {
    const trailName = window.trailName || 'this hike';
    if (!window.confirm(`Are you sure you want to delete ${trailName}? This action cannot be undone.`)) {
      return;
    }

    const hikeId = window.hike_id || window.id;
    if (!hikeId) {
      alert('Unable to delete hike: hike ID not found.');
      return;
    }

    try {
      await graph_client.request(DestroyHike, { input: { id: hikeId } });
      // Redirect to user's hikes page after successful deletion
      window.location.href = '/user/hikes';
    } catch (error: unknown) {
      console.error('Error deleting hike:', error);
      let errorMessage = 'Unknown error';
      if (error && typeof error === 'object') {
        const err = error as { response?: { errors?: Array<{ message?: string }> }; message?: string };
        errorMessage = err.response?.errors?.[0]?.message || err.message || errorMessage;
      }
      alert(`Failed to delete hike: ${errorMessage}`);
    }
  });
}

// Submit hike to be made official handler
const submitOfficialButton = document.getElementById('submit-official-hike');
if (submitOfficialButton) {
  submitOfficialButton.addEventListener('click', async () => {
    if (!window.confirm('Submit this hike to be reviewed and made official? Our team will review it before it appears in public search results.')) {
      return;
    }

    const hikeId = window.hike_id || window.id;
    if (!hikeId) {
      alert('Unable to submit hike: hike ID not found.');
      return;
    }

    try {
      const data = await graph_client.request(SubmitHikeForOfficial, { input: { id: hikeId } });
      const errors = data?.submitHikeForOfficial?.errors;
      if (errors && errors.length) {
        alert(`Failed to submit hike: ${errors.map((e: { errors: string[] }) => e.errors.join(', ')).join('; ')}`);
        return;
      }
      // Swap the action in place for the disabled "pending" state (mirrors the ERB),
      // so no full page reload is needed.
      const pending = document.createElement('span');
      pending.className = 'dropdown-item disabled text-muted';
      pending.textContent = 'Official request pending';
      submitOfficialButton.replaceWith(pending);
      alert('Hike submitted for official review.');
    } catch (error: unknown) {
      console.error('Error submitting hike for official review:', error);
      let errorMessage = 'Unknown error';
      if (error && typeof error === 'object') {
        const err = error as { response?: { errors?: Array<{ message?: string }> }; message?: string };
        errorMessage = err.response?.errors?.[0]?.message || err.message || errorMessage;
      }
      alert(`Failed to submit hike: ${errorMessage}`);
    }
  });
}
